import React, { useEffect, useState } from "react";
import { App, Button, Card, InputNumber, Segmented, Space, Typography } from "antd";
import { formatIls } from "../../../features/packageProfileMockup/mockPackageProfileData";
import {
  HUBWAYZ_COMMISSION_PREVIEW_NET_ILS,
  hubwayzAgentMarkupIls,
} from "../../../features/unifiedSearchMockup/hubwayz/resolveHubwayzHotelIlsPricing";
import {
  fetchHubwayzHotelSaleCommission,
  saveHubwayzHotelSaleCommission,
} from "../../../services/hubwayzHotelCommissionApi";

const { Text, Title } = Typography;

function HubwayzHotelSaleCommissionSettings() {
  const { message } = App.useApp();
  const [type, setType] = useState("percentage");
  const [value, setValue] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchHubwayzHotelSaleCommission()
      .then((data) => {
        if (cancelled) return;
        setType(data?.hubwayz_hotel_commission_type === "fixed_ils" ? "fixed_ils" : "percentage");
        setValue(Number(data?.hubwayz_hotel_commission_value) || 0);
      })
      .catch(() => {
        if (!cancelled) message.warning("לא נטענה עמלת המכירה. עד שמירה מוצלחת היא נשארת 0.");
      });
    return () => {
      cancelled = true;
    };
  }, [message]);

  const previewNet = HUBWAYZ_COMMISSION_PREVIEW_NET_ILS;
  const previewMarkup = hubwayzAgentMarkupIls(previewNet, { type, value: Number(value) || 0 });
  const previewSale = previewNet + previewMarkup;

  const save = async () => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0 || (type === "percentage" && numeric > 100)) {
      message.error("לא ניתן לשמור עמלה שלילית או ערך לא תקין. 0 מותר.");
      return;
    }
    setSaving(true);
    try {
      const saved = await saveHubwayzHotelSaleCommission({
        hubwayz_hotel_commission_type: type,
        hubwayz_hotel_commission_value: numeric,
      });
      setType(saved?.hubwayz_hotel_commission_type === "fixed_ils" ? "fixed_ils" : "percentage");
      setValue(Number(saved?.hubwayz_hotel_commission_value) || 0);
      message.success("עמלת המכירה למלונות נשמרה");
    } catch (error) {
      message.error(error?.response?.data?.message || "שגיאה בשמירת עמלת המכירה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <Title level={5}>עמלת מכירה למלונות</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        מתווספת למחיר הנטו שלך במלונות Hubwayz. היא לא משנה את מרווח HotelsRiver.
        אפשר לבחור אחוז או סכום קבוע, לא את שניהם יחד.
      </Text>
      <Space direction="vertical" style={{ width: "100%" }} size={8}>
        <Segmented
          value={type}
          onChange={(next) => setType(next)}
          options={[
            { label: "אחוז ממחיר הנטו שלי", value: "percentage" },
            { label: "סכום קבוע בשקלים להזמנה", value: "fixed_ils" },
          ]}
        />
        <InputNumber
          min={0}
          max={type === "percentage" ? 100 : 1000000}
          step={type === "percentage" ? 0.1 : 1}
          precision={2}
          style={{ width: "100%" }}
          addonAfter={type === "percentage" ? "%" : "₪"}
          value={value}
          onChange={(next) => setValue(next ?? 0)}
        />
        <Text type="secondary">
          תצוגה מקדימה לדוגמה על נטו של {formatIls(previewNet)}. זה אינו מחיר של הזמנה אמיתית.
        </Text>
        <Text>הנטו שלי: {formatIls(previewNet)}</Text>
        <Text>העמלה שלי: {formatIls(previewMarkup)}</Text>
        <Text>מחיר מכירה ללקוח: {formatIls(previewSale)}</Text>
        <Button type="primary" loading={saving} onClick={save}>
          שמור עמלת מכירה
        </Button>
      </Space>
    </Card>
  );
}

export default HubwayzHotelSaleCommissionSettings;
